<template>
  <div :id="themeId" class="related-products">
    <intersection-observer
      class="module-card-intersection-observer"
      :active="activateLogging"
      @intersect="emitIntersect"
    >
      <div
        v-if="headerText"
        class="related-products__header"
        :class="{ 'related-products__header-bg': headerBg }"
      >
        {{ headerText }}
      </div>
    </intersection-observer>
    <template v-if="image">
      <nuxt-link
        v-if="link"
        :to="link"
        class="related-products__banner"
        @click.native="onClickBanner"
      >
        <extended-image
          :src="image"
          css-width="100%"
          css-height="100%"
          has-loading
          object-fit="contain"
        />
      </nuxt-link>
      <span v-else class="related-products__banner" @click="onClickBanner">
        <extended-image
          :src="image"
          css-width="100%"
          css-height="100%"
          has-loading
          object-fit="contain"
        />
      </span>
    </template>
    <div v-if="date && !isInvalidDate" class="related-products__valid-period">
      <date v-bind="date" />
    </div>

    <div class="related-products__list">
      <product-card
        v-for="(item, index) in items"
        :key="item.uniqueIdAttribute"
        class="related-products__item"
        :title="item.title"
        :image="item.image"
        :price="item.price"
        :tax-included="item.taxIncluded"
        :notes="item.notes"
        :jan="item.jan"
        :shop-id="item.shopId ? item.shopId.toString() : null"
        :flyer-id="item.flyerId"
        :detail-id="item.detailId"
        :product-group-id="item.productGroupId"
        :activate-logging="activateProductLogging"
        :side-padding="sidePadding"
        :location="getProductLocation(index)"
        :utm-source="utmSource"
        @intersect="emitIntersectProduct"
      >
      </product-card>
    </div>
  </div>
</template>

<script setup>
import ProductCard from '@/components/ProductCard'
import ExtendedImage from '@/components/ExtendedImage'
import Date from '@/components/Date'
import IntersectionObserver from '@/components/IntersectionObserver'

import { useImpressionStore } from '../stores/impression';
const impressionStore = useImpressionStore()

const props = defineProps({
  /*
    * 関連記事用データ
    */
  items: {
    type: Array,
    required: true,
  },
  image: {
    type: String,
    default: '',
  },
  headerText: {
    type: String,
    default: '',
  },
  link: {
    type: String,
    required: true,
  },
  date: {
    type: Object,
    default: null,
  },
  id: {
    type: String,
    default: '',
  },
  uniqueIdAttribute: {
    type: String,
    default: '',
  },
  headerBg: {
    type: Boolean,
    default: false,
  },
  sidePadding: {
    type: Boolean,
    default: true,
  },
  /**
   * テーマのインプレッション計測の有効化
   */
  activateLogging: {
    type: Boolean,
    default: false,
  },
  /**
   * 商品のインプレッション計測の有効化
   */
  activateProductLogging: {
    type: Boolean,
    default: false,
  },
  themeLocation: {
    type: Object,
    default: null,
  },
  productLocation: {
    type: Object,
    default: null,
  },
  utmSource: {
    type: [Number, String, null],
    default: null,
  },
})

const emit = defineEmits(['intersect', 'intersect-product'])

const themeId = computed(() => {
  return `theme-${props.uniqueIdAttribute}`
})

const isInvalidDate = computed(() => {
  if (props.date.date.length === 2) {
    return props.date.date[0] === 'Invalid date' || props.date.date[1] === 'Invalid date'
  } else {
    return props.date.date[0] === 'Invalid date'
  }
})

const emitIntersect = () => {
  const data = {
    id: props.id,
    location: JSON.stringify(props.themeLocation),
  }
  emit('intersect', data)
}

const emitIntersectProduct = (data) => {
  emit('intersect-product', data)
}

const getProductLocation = (index) => {
  if (props.productLocation) {
    const newLocation = { ...props.productLocation }
    newLocation.element_line_number = index + 1
    return newLocation
  }
  return null
}

const onClickBanner = () => {
  if (props.themeLocation) {
    impressionStore.setLocation(props.themeLocation)
  }
}

</script>

<style lang="scss" scoped>
.related-products {
  position: relative;

  .related-products__banner {
    display: block;
    margin: 0 1.6rem;
    overflow: hidden;
    box-shadow: 0 0.2rem 0.4rem 0 rgba(0, 0, 0, 0.08);
    max-height: 68.6rem;
    width: 34.3rem;
    @media screen and (min-width: $tablet-breakpoint) {
      max-height: 144rem;
      width: 72rem;
    }
    @media screen and (min-width: $tablet2-breakpoint) {
      max-height: 121.6rem;
      width: 60.8rem;
    }
    @media screen and (min-width: $pc-breakpoint) {
      max-height: 160rem;
      width: 80rem;
    }

    & > img {
      display: block;
      object-fit: cover !important;
      // aspect-ratio: 16 / 5;
      object-position: top;
    }
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 0;
    }
  }

  .date {
    @media screen and (min-width: $tablet-breakpoint) {
      --lh: calc(23.68 / 16);
      --fz: 1.6rem;
    }
  }

  .related-products__header {
    font-weight: 700;
    font-size: 1.6rem;
    line-height: calc(23 / 16);
    padding: 0 1.6rem 0.8rem;
    @media screen and (min-width: $tablet-breakpoint) {
      font-size: 2rem;
      line-height: calc(29 / 20);
      padding: 0 0 0.8rem;
    }
  }

  .related-products__header-bg {
    @media screen and (min-width: $tablet-breakpoint) {
      background-color: $color-bg;
    }
  }

  .related-products__list {
    width: 100%;
  }

  .related-products__more-button-container {
    margin: 2rem 1.6rem 0;
  }
  .related-products__valid-period {
    padding: 0.4rem 1.6rem 0.8rem;
    @media screen and (min-width: $tablet-breakpoint) {
      padding: 0.8rem 0 1.6rem;
    }
  }
}
</style>
